/**
 * 請求鎖機制
 * 用於避免並發請求導致的認證問題
 *
 * 當偵測到 Token 正在刷新或登入正在進行時，
 * 將所有發出的請求暫存在一個 Queue 中，
 * 直到認證完成後再統一釋放
 */

type QueuedRequest = {
  resolve: () => void;
  reject: (error: unknown) => void;
  timestamp: number;
};

class RequestLock {
  private isLocked = false;
  private requestQueue: QueuedRequest[] = [];
  private lockReason: string | null = null;
  private maxQueueSize = 50; // 最大隊列長度
  private maxWaitTime = 10000; // 最大等待時間（10 秒）

  /**
   * 鎖定請求（例如：Token 正在刷新）
   */
  lock(reason: string = '認證進行中'): void {
    if (this.isLocked) {
      console.warn('[RequestLock] 請求已被鎖定，更新原因:', reason);
    } else {
      console.log('[RequestLock] 鎖定請求:', reason);
    }
    this.isLocked = true;
    this.lockReason = reason;
  }

  /**
   * 解鎖請求並釋放所有等待的請求
   */
  unlock(): void {
    if (!this.isLocked) {
      return;
    }

    console.log(
      '[RequestLock] 解鎖請求，釋放',
      this.requestQueue.length,
      '個等待的請求'
    );
    this.isLocked = false;
    const queue = [...this.requestQueue];
    this.requestQueue = [];
    this.lockReason = null;

    // 釋放所有等待的請求
    queue.forEach(request => {
      request.resolve();
    });
  }

  /**
   * 等待鎖釋放
   * @returns Promise，當鎖釋放時 resolve
   */
  async waitForUnlock(): Promise<void> {
    if (!this.isLocked) {
      return;
    }

    return new Promise((resolve, reject) => {
      // 檢查隊列長度
      if (this.requestQueue.length >= this.maxQueueSize) {
        reject(new Error('請求隊列已滿，請稍後再試'));
        return;
      }

      const request: QueuedRequest = {
        resolve,
        reject,
        timestamp: Date.now(),
      };

      this.requestQueue.push(request);

      // 設置超時
      setTimeout(() => {
        const index = this.requestQueue.indexOf(request);
        if (index !== -1) {
          this.requestQueue.splice(index, 1);
          reject(new Error(`等待鎖釋放超時（${this.maxWaitTime}ms）`));
        }
      }, this.maxWaitTime);
    });
  }

  /**
   * 檢查是否已鎖定
   */
  get locked(): boolean {
    return this.isLocked;
  }

  /**
   * 取得鎖定原因
   */
  get reason(): string | null {
    return this.lockReason;
  }

  /**
   * 清除所有等待的請求（用於錯誤處理）
   */
  clearQueue(): void {
    const queue = [...this.requestQueue];
    this.requestQueue = [];
    queue.forEach(request => {
      request.reject(new Error('請求隊列已清除'));
    });
  }
}

// 全域請求鎖實例
export const requestLock = new RequestLock();
